import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  qGlobalInvoiceHistory,
  qGlobalInvoiceOperationalKpis,
  qHomeBillingDetails,
  qHomeCollectionsToday,
  qHomeOutstanding,
  qInvoicesReadyToSettle,
  type GlobalInvoiceListRow,
  type GlobalInvoiceOperationalKpisRow,
  type HomeBillingDetailRow,
  type HomeCollectionsRow,
  type HomeOutstandingRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import { NavIcon, RightDrawer, type ShellOutletContext } from '../../shell/shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { InvoicesTab } from '../patient/consultation/InvoicesTab';
// PRD.218 — Record Payment from the global queue.
import { RecordPaymentDialog } from '../billing/RecordPaymentDialog';
import {
  InventoryListToolbar,
  InventoryPageShell,
  InventoryRow,
  InventoryStatusBadge,
  InventoryTable,
  type InventoryStatusVariant,
} from './_shared';

// PRD.216 — Global Invoice operational module.
//
// Frontdesk/billing-first work surface, parallel to Pharmacy:
//   /inventory/pharmacy   →  Ready to Dispense  /  Dispense History
//   /inventory/invoices   →  Ready to Settle    /  Invoice History
//
// Ready to Settle is the default landing tab. Inclusion rule for v1
// is `status IN ('draft', 'issued')` — partially-paid lives there until
// the invoice is marked paid (settlement is the action that moves it
// to History). NO stock movement is touched here.

type ViewMode = 'queue' | 'history';
type HistoryDateFilter = 'all' | 'today' | '7d' | '30d';
type HistoryStatusFilter = 'all' | 'paid' | 'issued' | 'draft';

const COLUMNS = [
  { label: 'Date', width: '110px' },
  { label: 'Invoice', width: '140px' },
  { label: 'Patient', width: '1.4fr' },
  { label: 'Status', width: '120px' },
  { label: 'Total', width: '110px', align: 'right' as const },
  { label: 'Paid', width: '110px', align: 'right' as const },
  { label: 'Balance', width: '120px', align: 'right' as const },
  { label: 'Actions', width: '120px', align: 'right' as const },
];

export function GlobalInvoicesPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };

  const { data: queueRows = [] } =
    usePowerSyncQuery<GlobalInvoiceListRow>(qInvoicesReadyToSettle());
  const { data: historyRows = [] } =
    usePowerSyncQuery<GlobalInvoiceListRow>(qGlobalInvoiceHistory());

  // PRD.216 — operational KPI strip. Reuses the same queries that power
  // the home Billing Snapshot so the numbers are always consistent
  // across surfaces. Today bounds are computed once per mount.
  const { dayStartUtc, dayEndUtc } = useMemo(todayBounds, []);
  const { data: outstandingRows = [] } = usePowerSyncQuery<HomeOutstandingRow>(qHomeOutstanding());
  const { data: collectionsRows = [] } = usePowerSyncQuery<HomeCollectionsRow>(
    qHomeCollectionsToday(dayStartUtc, dayEndUtc),
  );
  const { data: billingDetailRows = [] } =
    usePowerSyncQuery<HomeBillingDetailRow>(qHomeBillingDetails());
  const { data: operationalKpiRows = [] } = usePowerSyncQuery<GlobalInvoiceOperationalKpisRow>(
    qGlobalInvoiceOperationalKpis(),
  );
  const outstanding = outstandingRows[0] ?? { total_cents: 0, invoice_count: 0 };
  const collections = collectionsRows[0] ?? { total_cents: 0, payment_count: 0 };
  const billingDetail = billingDetailRows[0] ?? null;
  const operationalKpis = operationalKpiRows[0] ?? {
    overdue_total: 0,
    overdue_count: 0,
    draft_total: 0,
    draft_count: 0,
  };

  const [viewMode, setViewMode] = useState<ViewMode>('queue');
  const [search, setSearch] = useState('');
  const [historyDateFilter, setHistoryDateFilter] = useState<HistoryDateFilter>('all');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<HistoryStatusFilter>('all');
  // Right-drawer state for the in-place invoice editor (PRD.216 §6.4 —
  // Open Invoice without leaving the queue). Carries enough info to seed
  // InvoicesTab with the chosen invoice + a useful drawer title.
  const [drawerInvoice, setDrawerInvoice] = useState<GlobalInvoiceListRow | null>(null);
  // PRD.218 — Record Payment dialog, opened from a row "Pay" action.
  const [payInvoice, setPayInvoice] = useState<GlobalInvoiceListRow | null>(null);
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const filteredQueue = useMemo(
    () => filterRows(queueRows, search, 'all', 'all'),
    [queueRows, search],
  );
  const filteredHistory = useMemo(
    () => filterRows(historyRows, search, historyDateFilter, historyStatusFilter),
    [historyRows, search, historyDateFilter, historyStatusFilter],
  );

  const openInvoice = (row: GlobalInvoiceListRow) => {
    if (!row.patient_id) return;
    setDrawerInvoice(row);
  };

  return (
    <InventoryPageShell
      title="Invoices"
      subtitle="Operational queue for invoices that still need settlement, with a full history view for follow-up and audit."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
    >
      <GlobalInvoiceTabs
        viewMode={viewMode}
        onChange={(mode) => {
          setViewMode(mode);
          setSearch('');
        }}
        readyCount={queueRows.length}
        historyCount={historyRows.length}
      />

      <KpiStrip
        outstandingTotal={outstanding.total_cents ?? 0}
        outstandingCount={outstanding.invoice_count ?? 0}
        paymentsTotal={collections.total_cents ?? 0}
        paymentsCount={collections.payment_count ?? 0}
        overdueTotal={operationalKpis.overdue_total ?? 0}
        overdueCount={operationalKpis.overdue_count ?? 0}
        draftTotal={operationalKpis.draft_total ?? 0}
        draftCount={operationalKpis.draft_count ?? 0}
        oldestInvoiceNo={billingDetail?.oldest_invoice_no ?? null}
        oldestDays={billingDetail?.oldest_days ?? null}
        topPatientName={billingDetail?.top_patient_name ?? null}
      />

      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={
          viewMode === 'queue'
            ? 'Search invoice no, patient, reference...'
            : 'Search invoice no, patient, reference, status...'
        }
        filters={
          viewMode === 'history' ? (
            <>
              <select
                value={historyStatusFilter}
                onChange={(e) => setHistoryStatusFilter(e.target.value as HistoryStatusFilter)}
                style={selectStyle}
              >
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="issued">Issued</option>
                <option value="paid">Paid</option>
              </select>
              <select
                value={historyDateFilter}
                onChange={(e) => setHistoryDateFilter(e.target.value as HistoryDateFilter)}
                style={selectStyle}
              >
                <option value="all">All dates</option>
                <option value="today">Today</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
            </>
          ) : undefined
        }
      />

      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />

      {viewMode === 'queue' ? (
        <InvoiceListBody
          rows={filteredQueue}
          totalRowCount={queueRows.length}
          onOpen={openInvoice}
          onPay={setPayInvoice}
          emptyTitle="No invoices need settlement."
          emptyHint={
            queueRows.length === 0
              ? 'Invoices appear here as soon as they are created and stay until they are marked paid.'
              : 'Try a different search.'
          }
        />
      ) : (
        <InvoiceListBody
          rows={filteredHistory}
          totalRowCount={historyRows.length}
          onOpen={openInvoice}
          onPay={setPayInvoice}
          emptyTitle="No invoices match."
          emptyHint={
            historyRows.length === 0
              ? 'No invoices have been recorded yet.'
              : 'Try a different search or filter.'
          }
        />
      )}

      {/* PRD.216 §6.4 — Open Invoice in-place via the right drawer.
          Opens already expanded so the full invoice editor (line items,
          summary, notes) is visible without an extra click. The drawer
          renders the same InvoicesTab the patient page uses, scoped to
          the chosen invoice's patient and pre-selected to that invoice.
          NO stock movement is touched here — settlement still flows
          through the existing Mark Paid action inside InvoicesTab. */}
      <RightDrawer
        open={!!drawerInvoice}
        onClose={() => setDrawerInvoice(null)}
        title={
          drawerInvoice
            ? `Invoice ${drawerInvoice.invoice_no ?? drawerInvoice.id.slice(0, 8)}${
                drawerInvoice.patient_name ? ` · ${drawerInvoice.patient_name}` : ''
              }`
            : 'Invoice'
        }
        icon="file-text"
        expandable
        initialExpanded
        expandedWidth="min(1200px, 90%)"
        contentPadding="var(--sp-4)"
      >
        {drawerInvoice && drawerInvoice.patient_id && (
          <InvoicesTab
            key={drawerInvoice.id}
            patientId={drawerInvoice.patient_id}
            patientName={drawerInvoice.patient_name ?? ''}
            initialInvoiceId={drawerInvoice.id}
            hideHeader
            singlePane
          />
        )}
      </RightDrawer>

      {/* PRD.218 — Record Payment dialog wired from row "Pay" action.
          PRD.229 — invoice-locked here (payInvoice is always the selected row). */}
      <RecordPaymentDialog
        open={!!payInvoice}
        onOpenChange={(open) => {
          if (!open) setPayInvoice(null);
        }}
        invoice={
          payInvoice
            ? {
                id: payInvoice.id,
                invoice_no: payInvoice.invoice_no,
                grand_total: payInvoice.grand_total,
                status: payInvoice.status,
              }
            : null
        }
        patientId={payInvoice?.patient_id ?? ''}
        orgId={orgId}
      />
    </InventoryPageShell>
  );
}

// ─── Tab switcher ───

function GlobalInvoiceTabs({
  viewMode,
  onChange,
  readyCount,
  historyCount,
}: {
  viewMode: ViewMode;
  onChange: (mode: ViewMode) => void;
  readyCount: number;
  historyCount: number;
}) {
  const TABS: { value: ViewMode; label: string; count: number }[] = [
    { value: 'queue', label: 'Ready to Settle', count: readyCount },
    { value: 'history', label: 'Invoice History', count: historyCount },
  ];
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-6) 0',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      {TABS.map((t) => {
        const active = viewMode === t.value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              border: 'none',
              borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
              background: 'transparent',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: active ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: -1,
            }}
          >
            {t.label}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 22,
                height: 18,
                padding: '0 6px',
                borderRadius: 'var(--radius-full)',
                background: active ? 'var(--color-primary)' : 'var(--bg-surface-2)',
                color: active ? '#fff' : 'var(--text-muted)',
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Body / table ───

function InvoiceListBody({
  rows,
  totalRowCount,
  onOpen,
  onPay,
  emptyTitle,
  emptyHint,
}: {
  rows: GlobalInvoiceListRow[];
  totalRowCount: number;
  onOpen: (row: GlobalInvoiceListRow) => void;
  /** PRD.218 — Opens the Record Payment dialog for a row. Hidden for fully-paid rows. */
  onPay: (row: GlobalInvoiceListRow) => void;
  emptyTitle: string;
  emptyHint: string;
}) {
  const fmt = useFormatMoney();
  return (
    <InventoryTable
      columns={COLUMNS}
      empty={
        rows.length === 0
          ? {
              show: true,
              title: totalRowCount === 0 ? emptyTitle : 'No invoices match your filters.',
              hint: emptyHint,
            }
          : undefined
      }
    >
      {rows.map((row) => {
        const status = (row.status ?? 'draft') as 'draft' | 'issued' | 'paid';
        const total = row.grand_total ?? 0;
        const paid = row.paid_total ?? 0;
        const balance = row.balance ?? Math.max(0, total - paid);
        return (
          <InventoryRow
            key={row.id}
            columns={COLUMNS}
            clickable={!!row.patient_id}
            onClick={row.patient_id ? () => onOpen(row) : undefined}
          >
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {row.date_iso ?? row.created_at?.slice(0, 10) ?? '—'}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              {row.invoice_no ?? row.id.slice(0, 8)}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.patient_name ?? '—'}
              </div>
              {row.reference_no && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {row.reference_type === 'official' ? 'OR' : 'Ref'}: {row.reference_no}
                </div>
              )}
            </div>
            <InvoiceStatusBadge status={status} balance={balance} total={total} />
            <span style={amountStyle}>{fmt(total)}</span>
            <span
              style={{
                ...amountStyle,
                color: paid > 0 ? 'var(--color-success-text)' : 'var(--text-muted)',
              }}
            >
              {fmt(paid)}
            </span>
            <span
              style={{
                ...amountStyle,
                fontWeight: 'var(--font-weight-semi)',
                color:
                  balance <= 0.01
                    ? 'var(--color-success-text)'
                    : status === 'paid'
                      ? 'var(--text-muted)'
                      : '#b45309',
              }}
            >
              {fmt(Math.max(0, balance))}
            </span>
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}
              onClick={(e) => e.stopPropagation()}
            >
              {status !== 'paid' && balance > 0.005 && row.patient_id && (
                <button
                  type="button"
                  onClick={() => onPay(row)}
                  title="Record a payment for this invoice"
                  style={{
                    height: 30,
                    padding: '0 var(--sp-3)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid #16a34a',
                    background: 'var(--bg-surface)',
                    color: '#16a34a',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: 'pointer',
                  }}
                >
                  Pay
                </button>
              )}
              <button
                type="button"
                disabled={!row.patient_id}
                onClick={() => onOpen(row)}
                title={row.patient_id ? 'Open invoice' : 'Invoice has no patient link'}
                style={{
                  height: 30,
                  padding: '0 var(--sp-3)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-primary)',
                  background: 'var(--bg-surface)',
                  color: 'var(--color-primary)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semi)',
                  cursor: row.patient_id ? 'pointer' : 'not-allowed',
                  opacity: row.patient_id ? 1 : 0.5,
                }}
              >
                Open
              </button>
            </div>
          </InventoryRow>
        );
      })}
    </InventoryTable>
  );
}

// ─── Status badge ───
//
// Maps invoice status to one of the existing inventory status variants
// so the badge looks consistent with Pharmacy / Stock Card pills. A
// partially-paid 'issued' invoice surfaces as "Partial" so frontdesk
// can spot it at a glance without scanning the balance column.

function InvoiceStatusBadge({
  status,
  balance,
  total,
}: {
  status: 'draft' | 'issued' | 'paid';
  balance: number;
  total: number;
}) {
  if (status === 'paid') {
    return <InventoryStatusBadge status="completed" label="Paid" />;
  }
  if (status === 'issued' && balance > 0 && balance < total - 0.01) {
    return (
      <InventoryStatusBadge status="partial" label={`Partial (${pctPaid(total, balance)}%)`} />
    );
  }
  const variant: InventoryStatusVariant = status === 'issued' ? 'ordered' : 'draft';
  const label = status === 'issued' ? 'Issued' : 'Draft';
  return <InventoryStatusBadge status={variant} label={label} />;
}

function pctPaid(total: number, balance: number) {
  if (total <= 0) return 0;
  return Math.round(((total - balance) / total) * 100);
}

// ─── KPI strip ───
//
// Colored summary cards mirroring the home Billing Snapshot widget so
// the same metrics look the same wherever the user lands. Two primary
// cards (Unpaid Invoices red, Payments Today green) sit side-by-side,
// with a thin footer carrying the secondary "oldest unpaid / top
// unpaid patient" hints.

function KpiStrip({
  outstandingTotal,
  outstandingCount,
  paymentsTotal,
  paymentsCount,
  overdueTotal,
  overdueCount,
  draftTotal,
  draftCount,
  oldestInvoiceNo,
  oldestDays,
  topPatientName,
}: {
  outstandingTotal: number;
  outstandingCount: number;
  paymentsTotal: number;
  paymentsCount: number;
  overdueTotal: number;
  overdueCount: number;
  draftTotal: number;
  draftCount: number;
  oldestInvoiceNo: string | null;
  oldestDays: number | null;
  topPatientName: string | null;
}) {
  const fmtCompact = useFormatMoney({ compact: true });
  return (
    <div
      style={{
        padding: 'var(--sp-5) var(--sp-6)',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 'var(--sp-4)',
        }}
      >
        <KpiCard
          label="Unpaid Invoices"
          value={fmtCompact(outstandingTotal)}
          sub={`${outstandingCount} ${outstandingCount === 1 ? 'invoice' : 'invoices'}`}
          subColor="#dc2626"
          bg="#fef2f2"
          iconBg="#fecaca"
          iconColor="#dc2626"
          icon="alert-triangle"
        />
        <KpiCard
          label="Overdue (>30d)"
          value={fmtCompact(overdueTotal)}
          sub={`${overdueCount} ${overdueCount === 1 ? 'invoice' : 'invoices'}`}
          subColor="#c2410c"
          bg="#fff7ed"
          iconBg="#fed7aa"
          iconColor="#c2410c"
          icon="clock"
        />
        <KpiCard
          label="Drafts Pending"
          value={String(draftCount)}
          sub={draftCount === 0 ? 'All clear' : `${fmtCompact(draftTotal)} pending issue`}
          subColor="#475569"
          bg="#f8fafc"
          iconBg="#e2e8f0"
          iconColor="#475569"
          icon="edit"
        />
        <KpiCard
          label="Payments Today"
          value={fmtCompact(paymentsTotal)}
          sub={`${paymentsCount} ${paymentsCount === 1 ? 'payment' : 'payments'}`}
          subColor="#16a34a"
          bg="#f0fdf4"
          iconBg="#bbf7d0"
          iconColor="#16a34a"
          icon="credit-card"
        />
      </div>
      {(oldestInvoiceNo || topPatientName) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--sp-6)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            flexWrap: 'wrap',
          }}
        >
          {oldestInvoiceNo && (
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <span>Oldest unpaid:</span>
              <span
                style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-medium)' }}
              >
                {oldestInvoiceNo} ({oldestDays ?? 0} days)
              </span>
            </div>
          )}
          {topPatientName && (
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <span>Top unpaid patient:</span>
              <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
                {topPatientName}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  subColor,
  bg,
  iconBg,
  iconColor,
  icon,
}: {
  label: string;
  value: string;
  sub: string;
  subColor: string;
  bg: string;
  iconBg: string;
  iconColor: string;
  icon: string;
}) {
  return (
    <div
      style={{
        padding: 'var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        background: bg,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        minWidth: 0,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{label}</div>
        <div
          style={{
            fontSize: 'var(--font-size-xl, 24px)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            lineHeight: 1.2,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: subColor }}>{sub}</div>
      </div>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-full)',
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}
      >
        <NavIcon name={icon} />
      </div>
    </div>
  );
}

function todayBounds(): { dayStartUtc: string; dayEndUtc: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return { dayStartUtc: start.toISOString(), dayEndUtc: end.toISOString() };
}

// ─── Filter helpers ───

function filterRows(
  rows: GlobalInvoiceListRow[],
  search: string,
  dateFilter: HistoryDateFilter,
  statusFilter: HistoryStatusFilter,
): GlobalInvoiceListRow[] {
  const q = search.trim().toLowerCase();
  const now = Date.now();
  const dayMs = 86400000;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTodayMs = startOfToday.getTime();

  return rows.filter((row) => {
    if (statusFilter !== 'all' && row.status !== statusFilter) return false;
    if (dateFilter !== 'all') {
      const ref = row.date_iso ?? row.created_at;
      const t = ref ? new Date(ref).getTime() : 0;
      if (Number.isNaN(t) || t === 0) return false;
      if (dateFilter === 'today' && t < startOfTodayMs) return false;
      if (dateFilter === '7d' && now - t > 7 * dayMs) return false;
      if (dateFilter === '30d' && now - t > 30 * dayMs) return false;
    }
    if (!q) return true;
    return (
      (row.invoice_no ?? '').toLowerCase().includes(q) ||
      (row.patient_name ?? '').toLowerCase().includes(q) ||
      (row.reference_no ?? '').toLowerCase().includes(q) ||
      (row.status ?? '').toLowerCase().includes(q)
    );
  });
}

// ─── Styles ───

const amountStyle = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  textAlign: 'right' as const,
  fontVariantNumeric: 'tabular-nums' as const,
};

const selectStyle = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
};
